/**
 * videoService.ts
 *
 * Handles all video asset operations:
 * - Resolving the public URL for a self-hosted CBR video in Supabase Storage
 * - Falling back to a YouTube embed URL when no self-hosted video exists
 * - Upload helper (used by admin tooling, not end-users)
 *
 * Bucket layout in Supabase Storage (cbr-videos):
 *   cbr-videos/
 *     thumbnails/          ← WebP poster frames  (e.g. animation-cbr-official.webp)
 *     nl/                  ← Dutch narration videos
 *     en/                  ← English narration videos
 *     ar/                  ← Arabic narration videos
 *
 * File naming convention:
 *   {sectionId}__{videoId}__{lang}.mp4
 *   e.g. animation-questions__cbr-official-animation__nl.mp4
 */

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL!;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY!;
const BUCKET = 'cbr-videos';

export const supabaseVideoClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ─── Public URL helpers ───────────────────────────────────────

/**
 * Returns the public CDN URL for a self-hosted video file.
 * Returns null if the file does not exist yet (fallback to YouTube).
 */
export function getHostedVideoUrl(
  sectionId: string,
  videoId: string,
  lang: 'nl' | 'en' | 'ar' = 'nl'
): string {
  const filename = `${sectionId}__${videoId}__${lang}.mp4`;
  const { data } = supabaseVideoClient.storage
    .from(BUCKET)
    .getPublicUrl(`${lang}/${filename}`);
  return data.publicUrl;
}

/**
 * Returns the public CDN URL for a thumbnail image.
 */
export function getHostedThumbnailUrl(videoId: string): string {
  const { data } = supabaseVideoClient.storage
    .from(BUCKET)
    .getPublicUrl(`thumbnails/${videoId}.webp`);
  return data.publicUrl;
}

/**
 * YouTube embed URL with all branding minimised.
 * Used as a fallback until self-hosted video is uploaded.
 */
export function getYouTubeEmbedUrl(youtubeId: string): string {
  return (
    `https://www.youtube.com/embed/${youtubeId}` +
    `?autoplay=1&modestbranding=1&rel=0&iv_load_policy=3&cc_load_policy=0&color=white`
  );
}

// ─── Video availability check ─────────────────────────────────

const _availabilityCache: Record<string, boolean> = {};

/**
 * Checks whether a self-hosted video exists in Supabase Storage.
 * Results are cached for the session to avoid repeated HEAD requests.
 */
export async function isHostedVideoAvailable(
  sectionId: string,
  videoId: string,
  lang: 'nl' | 'en' | 'ar' = 'nl'
): Promise<boolean> {
  const filename = `${sectionId}__${videoId}__${lang}.mp4`;
  const path = `${lang}/${filename}`;

  if (_availabilityCache[path] !== undefined) {
    return _availabilityCache[path];
  }

  const { data, error } = await supabaseVideoClient.storage
    .from(BUCKET)
    .list(lang, { search: `${sectionId}__${videoId}__${lang}` });

  const exists = !error && Array.isArray(data) && data.length > 0;
  _availabilityCache[path] = exists;
  return exists;
}

// ─── Upload helper (admin only) ───────────────────────────────

export interface VideoUploadOptions {
  sectionId: string;
  videoId: string;
  lang: 'nl' | 'en' | 'ar';
  file: File;
  onProgress?: (percent: number) => void;
}

/**
 * Uploads a video file to Supabase Storage.
 * Requires an authenticated session (admin role).
 */
export async function uploadVideo({
  sectionId,
  videoId,
  lang,
  file,
}: VideoUploadOptions): Promise<{ url: string } | { error: string }> {
  const filename = `${sectionId}__${videoId}__${lang}.mp4`;
  const path = `${lang}/${filename}`;

  const { error } = await supabaseVideoClient.storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: '31536000', // 1 year CDN cache
      upsert: true,
      contentType: 'video/mp4',
    });

  if (error) return { error: error.message };

  const { data } = supabaseVideoClient.storage.from(BUCKET).getPublicUrl(path);
  delete _availabilityCache[path]; // clear cache after upload
  return { url: data.publicUrl };
}

/**
 * Uploads a thumbnail WebP image.
 */
export async function uploadThumbnail(
  videoId: string,
  file: File
): Promise<{ url: string } | { error: string }> {
  const path = `thumbnails/${videoId}.webp`;

  const { error } = await supabaseVideoClient.storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: '31536000',
      upsert: true,
      contentType: 'image/webp',
    });

  if (error) return { error: error.message };

  const { data } = supabaseVideoClient.storage.from(BUCKET).getPublicUrl(path);
  return { url: data.publicUrl };
}
